import { useQuery } from "@tanstack/react-query";
import { gql } from "../api/graphql.js";
import { INVOICES_QUERY } from "../api/queries.js";

export type DocumentType =
  | "invoice"
  | "proforma"
  | "payment_request"
  | "set_off"
  | "credit_note"
  | "delivery_note"
  | "receivables_confirmation"
  | "order"
  | "other";

export interface InvoiceRow {
  id: string;
  original_filename: string;
  s3_key: string;
  status: string;
  classification_uncertain: boolean;
  document_type: DocumentType;
  issue_date: string | null;
  due_date: string | null;
  supplier_name: string | null;
  supplier_id: string | null;
  total_amount: string | null;
  currency: string | null;
  variable_symbol: string | null;
  invoice_number: string | null;
  confidence: number | null;
  manually_corrected: boolean;
  created_at: string;
}

export interface InvoicesQueryParams {
  companyId: string;
  from: string;
  to: string;
  pageIndex: number;
  pageSize: number;
  orderByField: string;
  orderByDir: "asc" | "desc";
  supplierIlike: string;
  documentTypes: DocumentType[];
}

interface Resp {
  v_invoices_latest: InvoiceRow[];
  v_invoices_latest_aggregate: {
    aggregate: {
      count: number;
      sum: { total_amount: string | null };
    };
  };
}

export function useInvoices(params: InvoicesQueryParams) {
  return useQuery({
    queryKey: ["invoices", params],
    queryFn: async () => {
      const variables = {
        companyId: params.companyId,
        includeMissingSupplier: !params.supplierIlike,
        from: params.from,
        to: params.to,
        limit: params.pageSize,
        offset: params.pageIndex * params.pageSize,
        orderBy: [{ [params.orderByField]: params.orderByDir }],
        supplierIlike: params.supplierIlike
          ? `%${params.supplierIlike}%`
          : "%",
        // Pokud žádný typ není zaškrtnutý, nevracíme nic — uživatel by jinak
        // viděl všechny typy včetně těch, které explicitně odškrtl.
        documentTypes:
          params.documentTypes,
      };
      const data = await gql.request<Resp>(INVOICES_QUERY, variables);
      return {
        rows: data.v_invoices_latest,
        total: data.v_invoices_latest_aggregate.aggregate.count,
        totalSum: data.v_invoices_latest_aggregate.aggregate.sum.total_amount,
      };
    },
    enabled: Boolean(params.companyId),
  });
}
